// src/utils/reactSelectStyles.ts

export type ThemeMode = 'light' | 'dark';

export interface SelectOption {
    value: string;
    label: string;
}

// central palette for light/dark modes
const PALETTE: Record<ThemeMode, {
    background: { control: string; disabled: string; menu: string; optionHover: string };
    border: { default: string; focus: string; disabled: string };
    text: { default: string; placeholder: string; disabled: string; option: string };
}> = {
    light: {
        background: { control: 'transparent', disabled: '#e9ecef', menu: '#ffffff', optionHover: '#f8f9fa' },
        border: { default: '#ced4da', focus: '#2684FF', disabled: '#ced4da' },
        text: { default: '#495057', placeholder: '#6c757d', disabled: '#6c757d', option: '#212529' },
    },
    dark: {
        background: { control: 'transparent', disabled: '#495057', menu: '#343a40', optionHover: '#3e444a' },
        border: { default: '#495057', focus: '#4dabf7', disabled: '#6c757d' },
        text: { default: '#ffffff', placeholder: '#adb5bd', disabled: '#adb5bd', option: '#f8f9fa' },
    },
};

/**
 * Returns a styles config object for react-select that adapts to light/dark mode
 * and handles disabled, focused, hovered, and multi-value states.
 */
export function createSelectStyles(mode: ThemeMode) {
    const c = PALETTE[mode];

    return {
        control: (prov: any, state: any) => ({
            ...prov,
            backgroundColor: state.isDisabled ? c.background.disabled : c.background.control,
            borderColor: state.isDisabled
                ? c.border.disabled
                : state.isFocused
                    ? c.border.focus
                    : c.border.default,
            boxShadow: state.isFocused && !state.isDisabled
                ? `0 0 0 1px ${c.border.focus}`
                : undefined,
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            '&:hover': {
                borderColor: state.isDisabled ? c.border.disabled : c.border.focus,
            },
        }),

        input: (prov: any) => ({
            ...prov,
            color: c.text.default,
        }),

        placeholder: (prov: any, state: any) => ({
            ...prov,
            color: state.isDisabled ? c.text.disabled : c.text.placeholder,
        }),

        singleValue: (prov: any, state: any) => ({
            ...prov,
            color: state.isDisabled ? c.text.disabled : c.text.default,
            opacity: state.isDisabled ? 0.6 : 1,
        }),

        valueContainer: (prov: any) => ({
            ...prov,
            padding: '0 8px',
        }),

        dropdownIndicator: (prov: any, state: any) => ({
            ...prov,
            color: state.isDisabled ? c.text.disabled : c.text.default,
            padding: 4,
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        }),

        clearIndicator: (prov: any, state: any) => ({
            ...prov,
            color: state.isDisabled ? c.text.disabled : c.text.default,
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        }),

        indicatorsContainer: (prov: any) => ({
            ...prov,
            height: '32px',
        }),

        menu: (prov: any) => ({
            ...prov,
            backgroundColor: c.background.menu,
            zIndex: 10,
        }),

        menuList: (prov: any) => ({
            ...prov,
            padding: 0,
        }),

        option: (prov: any, state: any) => ({
            ...prov,
            backgroundColor: state.isFocused ? c.background.optionHover : c.background.menu,
            color: c.text.option,
            cursor: 'pointer',
        }),

        noOptionsMessage: (prov: any) => ({
            ...prov,
            color: c.text.placeholder,
        }),

        group: (prov: any) => ({
            ...prov,
            padding: 0,
        }),

        groupHeading: (prov: any) => ({
            ...prov,
            backgroundColor: c.background.menu,
            color: c.text.default,
            padding: '8px',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
        }),

        multiValue: (prov: any) => ({
            ...prov,
            backgroundColor: c.background.disabled,
        }),

        multiValueLabel: (prov: any) => ({
            ...prov,
            color: c.text.default,
        }),

        multiValueRemove: (prov: any, state: any) => ({
            ...prov,
            color: state.isDisabled ? c.text.disabled : c.text.default,
            cursor: 'pointer',
            ':hover': {
                backgroundColor: c.border.focus,
                color: 'white',
            },
        }),
    };
}